// SPDX-License-Identifier: AGPL-3.0-or-later
const ENABLE_WHEN_VALUE_KEYS = [
    'answerBoolean',
    'answerDecimal',
    'answerInteger',
    'answerDate',
    'answerDateTime',
    'answerTime',
    'answerString',
    'answerCoding',
    'answerQuantity',
    'answerReference',
];
/**
 * Reduce a polymorphic enableWhen `answer*` value to the primitive the
 * draft side stores, so `===` works. The validation engine
 * (packages/validation/src/conditional.ts) already extracts `.code`
 * from valueCoding before comparing; we mirror that here so
 * choice-gated dependents can be evaluated live, not just on submit.
 */
function projectExpected(key, raw) {
    if (key === 'answerCoding') {
        if (raw !== null && typeof raw === 'object') {
            const code = raw.code;
            return typeof code === 'string' ? code : null;
        }
        return null;
    }
    // The other value-keys are primitives already (string/number/boolean).
    // answerQuantity / answerReference fall through unchanged; the
    // renderer doesn't have first-class support for those gates yet, but
    // returning the raw object preserves prior behavior for any caller
    // that was relying on it.
    return raw;
}
function readExpected(when) {
    const record = when;
    for (const key of ENABLE_WHEN_VALUE_KEYS) {
        const v = record[key];
        if (v !== undefined)
            return projectExpected(key, v);
    }
    return undefined;
}
/**
 * Reduce a draft answer to the primitive the comparison expects. Choice
 * fields (SingleSelect, MultiSelect) write the full AnswerOption object
 * to the draft — we have to mirror the engine's behavior and pull the
 * stored value's code/string/etc. so the equality check works.
 */
function projectDraft(raw) {
    if (raw === null || raw === undefined)
        return raw;
    if (typeof raw !== 'object')
        return raw;
    const obj = raw;
    // AnswerOption variants. valueCoding wins because it's the most
    // common choice-field shape; the rest exist for less-common option
    // types (string/integer/date/time).
    const coding = obj['valueCoding'];
    if (coding !== null && typeof coding === 'object') {
        const code = coding.code;
        return typeof code === 'string' ? code : null;
    }
    if (typeof obj['valueString'] === 'string')
        return obj['valueString'];
    if (typeof obj['valueInteger'] === 'number')
        return obj['valueInteger'];
    if (typeof obj['valueDate'] === 'string')
        return obj['valueDate'];
    if (typeof obj['valueTime'] === 'string')
        return obj['valueTime'];
    // Arrays from MultiSelect: project each element and return as an
    // array — the comparators below treat arrays via `isPresent` for
    // `exists` and never reach the primitive-equality path for `=`/`!=`
    // (a multi-select gate compared with `=` to a scalar wouldn't make
    // semantic sense anyway).
    if (Array.isArray(raw))
        return raw.map(projectDraft);
    // Unknown shape — leave it alone so a caller passing a hand-rolled
    // value still gets its old (possibly-wrong) behavior rather than a
    // silent transformation.
    return raw;
}
function isPresent(value) {
    if (value === undefined || value === null)
        return false;
    if (typeof value === 'string')
        return value.length > 0;
    if (Array.isArray(value))
        return value.length > 0;
    return true;
}
function compareNumeric(actual, expected, op) {
    const a = Number(actual);
    const b = Number(expected);
    if (Number.isNaN(a) || Number.isNaN(b))
        return false;
    switch (op) {
        case '>':
            return a > b;
        case '<':
            return a < b;
        case '>=':
            return a >= b;
        case '<=':
            return a <= b;
    }
}
/** Evaluate a single `enableWhen` clause against the current draft. */
export function evaluateEnableWhen(when, draft) {
    // `isPresent` runs on the raw draft (an AnswerOption object counts
    // as "present" even before projection); everything else needs the
    // projected primitive so `===` works against the expected scalar.
    const rawActual = draft[when.question];
    const actual = projectDraft(rawActual);
    const expected = readExpected(when);
    switch (when.operator) {
        case 'exists':
            // FHIR R4: when `answerBoolean === false`, the clause inverts.
            return expected === false ? !isPresent(rawActual) : isPresent(rawActual);
        case '=':
            return actual === expected;
        case '!=':
            return actual !== expected;
        case '>':
        case '<':
        case '>=':
        case '<=':
            return compareNumeric(actual, expected, when.operator);
    }
}
/**
 * True iff `item` is active given the current `draft`. Items with no
 * `enableWhen` are always active; items with a clause list combine
 * results per `enableBehavior` (default `any`, per FHIR R4).
 */
export function isItemActive(item, draft) {
    const clauses = item.enableWhen;
    if (!clauses || clauses.length === 0)
        return true;
    const behavior = item.enableBehavior ?? 'any';
    if (behavior === 'all')
        return clauses.every((c) => evaluateEnableWhen(c, draft));
    return clauses.some((c) => evaluateEnableWhen(c, draft));
}
//# sourceMappingURL=enableWhen.js.map